import React, { useCallback, useEffect, useState } from 'react';
import StatCard from '../../../Components/StatCard';
import CommonLoader from '../../../Components/CommonLoader';
import NoDataPage from '../../../Components/NoDataPage';
import PageHeading from '../../../Components/PageHeading';
import CommonActionMenu from '../../../Components/CommonActionMenu';
import StatusBadge from '../../../Components/StatusBadge';
import { useToast } from '../../../Components/CommonToast';
import { useConfirm } from '../../../Components/ConfirmModal';
import { Body, SubHeading } from '../../../Constants/Fonts';
import { risk_portal_kri_list, risk_portal_delete_kri } from '../../../services';
import KriForm from '../Forms/KriForm';

const unwrap = (res) =>
    res?.data?.response_data?.data ?? res?.data?.data ??
    res?.statusCode?.response_data?.data ?? res?.statusCode?.data ?? null;

// KRI status → { colour token, StatusBadge variant }.
const STATUS = {
    normal: { color: 'var(--color-success)', variant: 'success' },
    warning: { color: 'var(--color-warning)', variant: 'warning' },
    breach: { color: 'var(--color-danger)', variant: 'danger' },
};
const statusOf = (s) => STATUS[s] || STATUS.normal;

const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

// One KRI card: name + risk id, current value, status pill, a threshold bar
// (fill = current value, markers = warning / breach), and last-updated footer.
function KriCard({ kri, canManage, onEdit, onDelete }) {
    const st = statusOf(kri.status);
    const maxVal = (Math.max(kri.current_value || 0, kri.threshold_breach || 0, kri.threshold_warning || 0) * 1.2) || 100;
    const pct = (v) => Math.min(100, Math.max(0, ((v || 0) / maxVal) * 100));
    const valPct = pct(kri.current_value);
    const warnPct = kri.threshold_warning != null ? pct(kri.threshold_warning) : null;
    const breachPct = kri.threshold_breach != null ? pct(kri.threshold_breach) : null;

    return (
        <div className="rp1_kri_card" style={{ borderLeft: `3px solid ${st.color}` }}>
            <div className="rp1_kri_card_head">
                <div style={{ minWidth: 0 }}>
                    <SubHeading variant={3} as="span" color="var(--color-text-primary)" style={{ display: 'block' }}>{kri.name}</SubHeading>
                    {kri.risk_id && <Body as="span" variant={2} color="var(--color-primary)" weight="semibold">{kri.risk_id}</Body>}
                </div>
                {canManage && (
                    <CommonActionMenu
                        actions={[
                            { label: 'Edit', onClick: () => onEdit(kri) },
                            { label: 'Delete', onClick: () => onDelete(kri), danger: true },
                        ]}
                    />
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                <Body as="span" weight="bold" color="var(--color-text-primary)" style={{ fontSize: 24, lineHeight: 1 }}>{kri.current_value ?? '—'}</Body>
                {kri.unit && <Body as="span" variant={2} color="var(--color-text-muted)">{kri.unit}</Body>}
                <StatusBadge variant={st.variant} label={(kri.status || 'normal').toUpperCase()} style={{ marginLeft: 'auto' }} />
            </div>

            {/* Threshold bar — fill to the current value, ticks at warning / breach. */}
            <div className="rp1_kri_bar_track">
                <div className="rp1_kri_bar_fill" style={{ width: `${valPct}%`, background: st.color }} />
                {warnPct != null && (
                    <div className="rp1_kri_bar_marker" style={{ left: `${warnPct}%`, background: 'var(--color-warning)' }} title={`Warning: ${kri.threshold_warning}`} />
                )}
                {breachPct != null && (
                    <div className="rp1_kri_bar_marker" style={{ left: `${breachPct}%`, background: 'var(--color-danger)' }} title={`Breach: ${kri.threshold_breach}`} />
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Body as="span" variant={2} color="var(--color-text-muted)">0</Body>
                <Body as="span" variant={2} color="var(--color-text-muted)">
                    {kri.threshold_warning != null && `Warning ${kri.threshold_warning}`}
                    {kri.threshold_warning != null && kri.threshold_breach != null && '  ·  '}
                    {kri.threshold_breach != null && `Breach ${kri.threshold_breach}`}
                </Body>
                <Body as="span" variant={2} color="var(--color-text-muted)">{Math.round(maxVal)}</Body>
            </div>

            <Body as="span" variant={2} color="var(--color-text-muted)">Updated {fmtTime(kri.last_updated)}</Body>
        </div>
    );
}

// KeyRiskIndicators — org-wide KRI monitoring. Summary KPIs + a card per KRI
// with its threshold bar. KRIs are created from a risk's detail view (bound to
// a risk); here they can be updated (value / thresholds) or removed.
export default function KeyRiskIndicators({ canManage = false }) {
    const [kris, setKris] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [editing, setEditing] = useState(null);

    const { notify, toastNode } = useToast();
    const { confirm, confirmModal } = useConfirm();

    const load = useCallback(async () => {
        try {
            const d = unwrap(await risk_portal_kri_list());
            setKris(Array.isArray(d) ? d : (Array.isArray(d?.kris) ? d.kris : []));
        } catch (e) {
            notify('Failed to load KRIs', 'error');
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => { setLoading(true); load(); }, [load]);

    const remove = async (k) => {
        const ok = await confirm({
            title: 'Delete KRI',
            message: `Delete "${k.name}"? This can't be undone.`,
            confirmLabel: 'Delete', danger: true, themeColor: false,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await risk_portal_delete_kri(k.id);
            notify(`KRI "${k.name}" deleted`, 'success');
            await load();
        } catch (e) {
            notify(e?.response?.data?.error?.message || e?.message || 'Failed to delete KRI', 'error');
        } finally {
            setBusy(false);
        }
    };

    const countBy = (s) => kris.filter((k) => k.status === s).length;

    if (loading) return <CommonLoader fullPage label="Loading KRIs…" />;

    return (
        <div className="rp1_kri">
            <PageHeading
                title="Key Risk Indicators"
                subtitle="Track leading indicators against warning and breach thresholds."
            />

            <div className="rp1_stat_row rp1_stat_row--4">
                <StatCard labelBelow label="Total KRIs" value={kris.length} valueColor="var(--color-primary)" valueSize={36} />
                <StatCard labelBelow label="Normal" value={countBy('normal')} valueColor="var(--color-success)" valueSize={36} />
                <StatCard labelBelow label="Warning" value={countBy('warning')} valueColor="var(--color-warning)" valueSize={36} />
                <StatCard labelBelow label="Breach" value={countBy('breach')} valueColor="var(--color-danger)" valueSize={36} />
            </div>

            {kris.length === 0 ? (
                <NoDataPage
                    title="No KRIs defined"
                    hint="Add Key Risk Indicators from a risk's detail view to start monitoring thresholds here."
                />
            ) : (
                <div className="rp1_kri_grid">
                    {kris.map((k) => (
                        <KriCard
                            key={k.id}
                            kri={k}
                            canManage={canManage}
                            onEdit={setEditing}
                            onDelete={remove}
                        />
                    ))}
                </div>
            )}

            <KriForm
                show={!!editing}
                kri={editing}
                onClose={() => setEditing(null)}
                onSuccess={(msg) => { notify(msg, 'success'); load(); }}
                onError={(msg) => notify(msg, 'error')}
            />

            {busy && <CommonLoader fullPage label="Working…" />}
            {confirmModal}
            {toastNode}
        </div>
    );
}
